import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Film,
  X,
  Loader2,
  CircleCheck,
  CircleAlert,
} from "lucide-react";
import { useHlsJobs } from "contexts/HlsJobsContext";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";

const ENTITY_LABELS = {
  formation_trailer: "Trailer formation",
  module: "Vidéo module",
  screen: "Vidéo écran",
};

const STATUS_COLOR = {
  queued: V2.muted,
  processing: V2.accent,
  completed: "#1BA672",
  failed: "#E5484D",
};

function JobRow({ job }) {
  const color = STATUS_COLOR[job.status] || V2.muted;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        py: 1,
        px: 1.5,
        borderBottom: `1px solid ${V2.line}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {job.status === "processing" && (
          <Box
            component={Loader2}
            size={14}
            color={color}
            sx={{
              animation: "hlsJobsSpin 1s linear infinite",
              "@keyframes hlsJobsSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
        {job.status === "completed" && <CircleCheck size={14} color={color} />}
        {job.status === "failed" && <CircleAlert size={14} color={color} />}
        {job.status === "queued" && <Film size={14} color={color} />}
        <Typography
          variant="caption"
          sx={{
            fontFamily: V2.fontBody,
            color: V2.ink,
            fontWeight: 600,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={job.label}
        >
          {job.label || ENTITY_LABELS[job.entity_type] || "Vidéo"}
        </Typography>
        <Chip
          label={
            job.status === "queued"
              ? "En attente"
              : job.status === "processing"
                ? `${job.progress ?? 0}%`
                : job.status === "completed"
                  ? "Terminé"
                  : "Échoué"
          }
          size="small"
          sx={{
            height: 18,
            fontSize: "0.65rem",
            bgcolor: `${color}1A`,
            color,
            fontWeight: 600,
          }}
        />
      </Box>
      {job.status === "processing" && (
        <LinearProgress
          variant="determinate"
          value={job.progress ?? 0}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: `${color}22`,
            "& .MuiLinearProgress-bar": { bgcolor: color },
          }}
        />
      )}
      {job.status === "failed" && job.error && (
        <Typography
          variant="caption"
          sx={{ color: "#E5484D", fontSize: "0.68rem" }}
        >
          {job.error}
        </Typography>
      )}
      {job.stage && job.status === "processing" && (
        <Typography variant="caption" sx={{ color: V2.muted, fontSize: "0.65rem" }}>
          {job.stage}
        </Typography>
      )}
    </Box>
  );
}

export default function HlsJobsPanel() {
  const { activeJobs, recentJobs } = useHlsJobs();
  const [collapsed, setCollapsed] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const allJobs = [...activeJobs, ...recentJobs.slice(0, 5)];

  if (allJobs.length === 0 || dismissed) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 320,
        maxWidth: "calc(100vw - 40px)",
        borderRadius: "12px",
        overflow: "hidden",
        zIndex: 1400,
        border: `1px solid ${V2.line}`,
      }}
    >
      <Box
        onClick={() => setCollapsed((c) => !c)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: V2.surfaceSolid,
          cursor: "pointer",
        }}
      >
        <Film size={16} color={V2.accent} />
        <Typography
          variant="subtitle2"
          sx={{ fontFamily: V2.fontBody, color: V2.ink, flex: 1 }}
        >
          Conversions vidéo{" "}
          {activeJobs.length > 0 ? `(${activeJobs.length})` : ""}
        </Typography>
        <IconButton size="small" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
        <Tooltip title="Masquer">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
          >
            <X size={16} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={!collapsed}>
        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          {allJobs.map((job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
